import React, { useState, useEffect, useRef } from "react";
import { Card as RawCard } from "@/components/ui/card";
import { Button as RawButton } from "@/components/ui/button";
import { Input as RawInput } from "@/components/ui/input";
import { Label as RawLable } from "@/components/ui/lable";
import { Calendar } from "@/components/ui/calender";
import {
  Popover,
  PopoverContent as RawPopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Calendar as CalendarIcon,
  Loader2,
  CheckCircle2,
  Pencil,
  X,
  UploadCloud,
  FileText,
} from "lucide-react";
import { format } from "date-fns";
import { motion } from "framer-motion";
import StatusDialog from "../ui/statusloading";
import { COUNTRIES } from "@/data/countries";
import {
  uploadKtpDocument,
  KTP_MAX_TOTAL_SIZE,
} from "../../services/profileService";
import {
  isValidPassportName,
  isValidPassportNumber,
  isPastDate,
  isExpiryWithinTenYears,
  GENDER_OPTIONS,
} from "@/utils/validators";

const INDONESIA = "Indonesia";

/**
 * @typedef {Object} PassportDetails
 * @property {string} [passport_number]
 * @property {string} [full_name_on_passport]
 * @property {string} [nationality]
 * @property {string} [birth_country]
 * @property {string} [gender]
 * @property {string} [date_of_birth] - ISO date string, e.g. "1990-01-31"
 * @property {string} [passport_date_of_issue] - ISO date string
 * @property {string} [passport_expiry_date] - ISO date string, e.g. "2030-06-15"
 * @property {boolean} [is_batam_local] - Only meaningful when nationality is Indonesia
 * @property {string} [ktp_front_url]
 * @property {string} [ktp_back_url]
 *
 * @typedef {Object} UserProfile
 * @property {string} [fullName]
 * @property {number} [golf_handicap]
 * @property {string} [profile_photo_url]
 * @property {string} [email]
 * @property {PassportDetails} [passport_details]
 *
 * @typedef {Object} PassportSectionProps
 * @property {UserProfile} [user]
 * @property {string} [uid]
 * @property {(data: { passport_details: PassportDetails }) => Promise<any>} onUpdate
 */
export default function PassportSection(
  /** @type {PassportSectionProps} */ { user, uid, onUpdate },
) {
  /** @type {PassportDetails} */
  const passportData = user?.passport_details || {};

  const [passportNumber, setPassportNumber] = useState(
    passportData.passport_number || "",
  );
  const [fullNameOnPassport, setFullNameOnPassport] = useState(
    passportData.full_name_on_passport || "",
  );
  const [nationality, setNationality] = useState(
    passportData.nationality || "",
  );
  const [birthCountry, setBirthCountry] = useState(
    passportData.birth_country || "",
  );
  const [gender, setGender] = useState(passportData.gender || "");
  const [isBatamLocal, setIsBatamLocal] = useState(
    passportData.is_batam_local === true
      ? "Yes"
      : passportData.is_batam_local === false
        ? "No"
        : "",
  );
  const [ktpFrontUrl, setKtpFrontUrl] = useState(
    passportData.ktp_front_url || "",
  );
  const [ktpBackUrl, setKtpBackUrl] = useState(passportData.ktp_back_url || "");
  // Byte sizes of whatever's currently uploaded this session, used to enforce
  // the combined 5MB budget across both files — reset per side on replace.
  const [ktpFrontSize, setKtpFrontSize] = useState(0);
  const [ktpBackSize, setKtpBackSize] = useState(0);
  const [isUploadingKtpFront, setIsUploadingKtpFront] = useState(false);
  const [isUploadingKtpBack, setIsUploadingKtpBack] = useState(false);
  const [dateOfBirth, setDateOfBirth] = useState(
    passportData.date_of_birth ? new Date(passportData.date_of_birth) : null,
  );
  const [dateOfIssue, setDateOfIssue] = useState(
    passportData.passport_date_of_issue
      ? new Date(passportData.passport_date_of_issue)
      : null,
  );
  const [expiryDate, setExpiryDate] = useState(
    passportData.passport_expiry_date
      ? new Date(passportData.passport_expiry_date)
      : null,
  );
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // View vs. edit mode. Starts in edit mode only if there's no saved
  // passport number yet (first-time entry); otherwise starts read-only.
  const [isEditing, setIsEditing] = useState(!passportData.passport_number);

  const [dialog, setDialog] = useState({
    open: false,
    variant: /** @type {"success" | "error"} */ ("success"),
    title: "",
    description: "",
  });

  // Re-sync when user data arrives/changes after this component has
  // already mounted (e.g. async Firestore fetch resolving post-render,
  // or a fresh `user` object coming back after handleSave completes).
  useEffect(() => {
    /** @type {PassportDetails} */
    const pd = user?.passport_details || {};
    setPassportNumber(pd.passport_number || "");
    setFullNameOnPassport(pd.full_name_on_passport || "");
    setNationality(pd.nationality || "");
    setBirthCountry(pd.birth_country || "");
    setGender(pd.gender || "");
    setIsBatamLocal(
      pd.is_batam_local === true
        ? "Yes"
        : pd.is_batam_local === false
          ? "No"
          : "",
    );
    setKtpFrontUrl(pd.ktp_front_url || "");
    setKtpBackUrl(pd.ktp_back_url || "");
    setDateOfBirth(pd.date_of_birth ? new Date(pd.date_of_birth) : null);
    setDateOfIssue(
      pd.passport_date_of_issue ? new Date(pd.passport_date_of_issue) : null,
    );
    setExpiryDate(
      pd.passport_expiry_date ? new Date(pd.passport_expiry_date) : null,
    );
    // Only saved data should land in read-only mode. If nothing's saved
    // yet, keep/open the form so the user can fill it in.
    setIsEditing(!pd.passport_number);
  }, [user]);

  const Card = /** @type {React.ComponentType<any>} */ (RawCard);
  const Button = /** @type {React.ComponentType<any>} */ (RawButton);
  const Label = /** @type {React.ComponentType<any>} */ (RawLable);
  const Input = /** @type {React.ComponentType<any>} */ (RawInput);
  const PopoverContent = /** @type {React.ComponentType<any>} */ (
    RawPopoverContent
  );

  const validate = () => {
    const nextErrors = {};

    if (!fullNameOnPassport.trim()) {
      nextErrors.fullNameOnPassport = "Please fill in your Name as in Passport";
    } else if (!isValidPassportName(fullNameOnPassport)) {
      nextErrors.fullNameOnPassport =
        "Special characters and numbers are not allowed";
    }

    if (!passportNumber.trim()) {
      nextErrors.passportNumber = "Please fill in your Passport Number";
    } else if (!isValidPassportNumber(passportNumber)) {
      nextErrors.passportNumber = "Special characters are not allowed";
    }

    if (!nationality) {
      nextErrors.nationality = "Please select your Nationality";
    }

    if (!birthCountry) {
      nextErrors.birthCountry = "Please select your Birth Country";
    }

    if (nationality === INDONESIA) {
      if (!isBatamLocal) {
        nextErrors.isBatamLocal =
          "Please indicate if you are a Batam Local with KTP Batam";
      } else if (isBatamLocal === "Yes") {
        if (!ktpFrontUrl) {
          nextErrors.ktpFrontUrl =
            "Please upload the front view of your KTP card";
        }
        if (!ktpBackUrl) {
          nextErrors.ktpBackUrl =
            "Please upload the back view of your KTP card";
        }
      }
    }

    if (!gender) {
      nextErrors.gender = "Please choose your Gender";
    }

    if (!dateOfBirth) {
      nextErrors.dateOfBirth = "Please select your Date of Birth";
    } else if (!isPastDate(dateOfBirth)) {
      nextErrors.dateOfBirth = "Date of Birth must be in the past";
    }

    if (!dateOfIssue) {
      nextErrors.dateOfIssue = "Please select your Passport Date of Issue";
    }

    if (!expiryDate) {
      nextErrors.expiryDate = "Please select your Passport Date of Expiry";
    } else if (!isExpiryWithinTenYears(dateOfIssue, expiryDate)) {
      nextErrors.expiryDate =
        "Passport expiry date must not be more than 10 years from the date of issue";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

const handleSave = async () => {
  if (!validate()) return;

  setIsSaving(true);
  try {
    const isIndonesian = nationality === INDONESIA;
    const isBatam = isIndonesian && isBatamLocal === "Yes";

    /** @type {PassportDetails} */
    const nextPassportDetails = {
      passport_number: passportNumber,
      full_name_on_passport: fullNameOnPassport,
      nationality: nationality,
      birth_country: birthCountry,
      gender: gender,
      date_of_birth: dateOfBirth ? format(dateOfBirth, "yyyy-MM-dd") : undefined,
      passport_date_of_issue: dateOfIssue ? format(dateOfIssue, "yyyy-MM-dd") : undefined,
      passport_expiry_date: expiryDate ? format(expiryDate, "yyyy-MM-dd") : undefined,
    };

    if (isIndonesian) {
      nextPassportDetails.is_batam_local = isBatamLocal === "Yes";
    }
    if (isBatam) {
      nextPassportDetails.ktp_front_url = ktpFrontUrl;
      nextPassportDetails.ktp_back_url = ktpBackUrl;
    }

    // Strip any remaining undefined values (belt-and-suspenders —
    // validate() should already guarantee the date fields are set,
    // but this keeps setDoc from ever choking on a stray undefined).
    Object.keys(nextPassportDetails).forEach((key) => {
      if (nextPassportDetails[key] === undefined) {
        delete nextPassportDetails[key];
      }
    });

    await onUpdate({ passport_details: nextPassportDetails });

    setDialog({
      open: true,
      variant: "success",
      title: "Passport details saved",
      description: "Your passport information has been saved successfully.",
    });
    setIsEditing(false);
  } catch (error) {
    console.error("Update error:", error);
    setDialog({
      open: true,
      variant: "error",
      title: "Save failed",
      description: "Failed to save passport details. Please try again.",
    });
  } finally {
    setIsSaving(false);
  }
};

  // Clears the Batam-local follow-up answer + any uploaded KTP images
  // whenever nationality changes away from Indonesia, so stale data can't
  // get saved for a nationality it no longer applies to.
  const handleNationalityChange = (value) => {
    setNationality(value);
    if (value !== INDONESIA) {
      setIsBatamLocal("");
      setKtpFrontUrl("");
      setKtpBackUrl("");
    }
  };

  const handleKtpUpload = async (side, file) => {
    if (!file) return;
    if (!uid) {
      setDialog({
        open: true,
        variant: "error",
        title: "Upload failed",
        description:
          "Can't upload right now — user not identified. Try refreshing.",
      });
      return;
    }

    const setUploading =
      side === "front" ? setIsUploadingKtpFront : setIsUploadingKtpBack;
    const setUrl = side === "front" ? setKtpFrontUrl : setKtpBackUrl;
    const setSize = side === "front" ? setKtpFrontSize : setKtpBackSize;
    const existingUrl = side === "front" ? ktpFrontUrl : ktpBackUrl;
    const otherSideSize = side === "front" ? ktpBackSize : ktpFrontSize;

    if (otherSideSize + file.size > KTP_MAX_TOTAL_SIZE) {
      setDialog({
        open: true,
        variant: "error",
        title: "Upload too large",
        description:
          "KTP front and back must together be under 5MB. Try a smaller or more compressed file.",
      });
      return;
    }

    setUploading(true);
    try {
      const url = await uploadKtpDocument(uid, file, side, existingUrl);
      setUrl(url);
      setSize(file.size);
    } catch (err) {
      console.error("KTP upload failed:", err);
      setDialog({
        open: true,
        variant: "error",
        title: "Upload failed",
        description: err.message || "Failed to upload image. Please try again.",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    // Revert any unsaved edits back to the last saved values.
    setPassportNumber(passportData.passport_number || "");
    setFullNameOnPassport(passportData.full_name_on_passport || "");
    setNationality(passportData.nationality || "");
    setBirthCountry(passportData.birth_country || "");
    setGender(passportData.gender || "");
    setIsBatamLocal(
      passportData.is_batam_local === true
        ? "Yes"
        : passportData.is_batam_local === false
          ? "No"
          : "",
    );
    setKtpFrontUrl(passportData.ktp_front_url || "");
    setKtpBackUrl(passportData.ktp_back_url || "");
    setDateOfBirth(
      passportData.date_of_birth ? new Date(passportData.date_of_birth) : null,
    );
    setDateOfIssue(
      passportData.passport_date_of_issue
        ? new Date(passportData.passport_date_of_issue)
        : null,
    );
    setExpiryDate(
      passportData.passport_expiry_date
        ? new Date(passportData.passport_expiry_date)
        : null,
    );
    setErrors({});
    setIsEditing(false);
  };

  const hasPassport = Boolean(passportData.passport_number);

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Card className="p-6 bg-white border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-[#0B2421]">
              Passport Profile
            </h2>
            <div className="flex items-center gap-3">
              {hasPassport && !isEditing && (
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle2 className="h-5 w-5" />
                  <span className="text-sm font-semibold">Saved</span>
                </div>
              )}
              {!isEditing && (
                <Button
                  variant="outline"
                  onClick={() => setIsEditing(true)}
                  className="h-9 px-3"
                >
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              )}
              {isEditing && hasPassport && (
                <Button
                  variant="ghost"
                  onClick={handleCancel}
                  disabled={isSaving}
                  className="h-9 px-3"
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
              )}
            </div>
          </div>

          <p className="mb-6 text-gray-500">
            Save your passport details for quick booking when traveling
            internationally.
          </p>

          {!isEditing ? (
            // ---------- READ-ONLY VIEW ----------
            <div className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-1">
                    Passport Number
                  </p>
                  <p className="text-gray-700">
                    {passportData.passport_number || "—"}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-1">
                    Full Name (as on passport)
                  </p>
                  <p className="text-gray-700">
                    {passportData.full_name_on_passport || "—"}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-1">
                    Nationality
                  </p>
                  <p className="text-gray-700">
                    {passportData.nationality || "—"}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-1">
                    Birth Country
                  </p>
                  <p className="text-gray-700">
                    {passportData.birth_country || "—"}
                  </p>
                </div>
                {passportData.nationality === INDONESIA && (
                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-1">
                      Batam Local (KTP Batam)
                    </p>
                    <p className="text-gray-700">
                      {passportData.is_batam_local === true
                        ? "Yes"
                        : passportData.is_batam_local === false
                          ? "No"
                          : "—"}
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-1">
                    Gender
                  </p>
                  <p className="text-gray-700">{passportData.gender || "—"}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-1">
                    Date of Birth
                  </p>
                  <p className="text-gray-700">
                    {passportData.date_of_birth
                      ? format(
                          new Date(passportData.date_of_birth),
                          "dd MMM yyyy",
                        )
                      : "—"}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-1">
                    Passport Date of Issue
                  </p>
                  <p className="text-gray-700">
                    {passportData.passport_date_of_issue
                      ? format(
                          new Date(passportData.passport_date_of_issue),
                          "dd MMM yyyy",
                        )
                      : "—"}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-1">
                    Passport Expiry Date
                  </p>
                  <p className="text-gray-700">
                    {passportData.passport_expiry_date
                      ? format(
                          new Date(passportData.passport_expiry_date),
                          "dd MMM yyyy",
                        )
                      : "—"}
                  </p>
                </div>
              </div>
              {passportData.is_batam_local === true && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">
                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-1">
                      KTP Front View
                    </p>
                    {passportData.ktp_front_url ? (
                      passportData.ktp_front_url
                        .toLowerCase()
                        .includes(".pdf") ? (
                        <div className="h-32 w-full max-w-[12rem] rounded-xl border border-gray-200 bg-gray-50 flex flex-col items-center justify-center gap-1">
                          <FileText className="h-6 w-6 text-gray-400" />
                          <span className="text-xs text-gray-500">
                            PDF uploaded
                          </span>
                        </div>
                      ) : (
                        <img
                          src={passportData.ktp_front_url}
                          alt="KTP front"
                          className="h-32 rounded-xl border border-gray-200 object-cover"
                        />
                      )
                    ) : (
                      <p className="text-gray-700">—</p>
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-1">
                      KTP Back View
                    </p>
                    {passportData.ktp_back_url ? (
                      passportData.ktp_back_url
                        .toLowerCase()
                        .includes(".pdf") ? (
                        <div className="h-32 w-full max-w-[12rem] rounded-xl border border-gray-200 bg-gray-50 flex flex-col items-center justify-center gap-1">
                          <FileText className="h-6 w-6 text-gray-400" />
                          <span className="text-xs text-gray-500">
                            PDF uploaded
                          </span>
                        </div>
                      ) : (
                        <img
                          src={passportData.ktp_back_url}
                          alt="KTP back"
                          className="h-32 rounded-xl border border-gray-200 object-cover"
                        />
                      )
                    ) : (
                      <p className="text-gray-700">—</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            // ---------- EDIT FORM ----------
            <div className="space-y-5">
              <div>
                <Label
                  htmlFor="passportNumber"
                  className="text-sm font-semibold text-gray-700 mb-2 block"
                >
                  Passport Number *
                </Label>
                <Input
                  id="passportNumber"
                  type="text"
                  value={passportNumber}
                  onChange={
                    /** @param {any} e */ (e) =>
                      setPassportNumber(e.target.value.toUpperCase())
                  }
                  placeholder="e.g., A12345678"
                  className="h-12 text-gray-700"
                />
                {errors.passportNumber && (
                  <p className="text-xs text-red-500 mt-1">
                    {errors.passportNumber}
                  </p>
                )}
              </div>

              <div>
                <Label
                  htmlFor="fullNamePassport"
                  className="text-sm font-semibold text-gray-700 mb-2 block"
                >
                  Full Name (as on passport) *
                </Label>
                <Input
                  id="fullNamePassport"
                  type="text"
                  value={fullNameOnPassport}
                  onChange={
                    /** @param {any} e */ (e) =>
                      setFullNameOnPassport(e.target.value.toUpperCase())
                  }
                  placeholder="Full name exactly as shown on passport"
                  className="h-12 text-gray-700"
                />
                {errors.fullNameOnPassport && (
                  <p className="text-xs text-red-500 mt-1">
                    {errors.fullNameOnPassport}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-semibold text-gray-700 mb-2 block">
                    Nationality *
                  </Label>
                  <Select
                    value={nationality}
                    onValueChange={handleNationalityChange}
                  >
                    <SelectTrigger className="h-12 text-gray-700">
                      <SelectValue placeholder="Select nationality" />
                    </SelectTrigger>
                    <SelectContent className="bg-white text-black max-h-72">
                      {COUNTRIES.map((c) => (
                        <SelectItem key={c.name} value={c.name}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.nationality && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.nationality}
                    </p>
                  )}
                </div>

                <div>
                  <Label className="text-sm font-semibold text-gray-700 mb-2 block">
                    Birth Country *
                  </Label>
                  <Select value={birthCountry} onValueChange={setBirthCountry}>
                    <SelectTrigger className="h-12 text-gray-700">
                      <SelectValue placeholder="Select birth country" />
                    </SelectTrigger>
                    <SelectContent className="bg-white text-black max-h-72">
                      {COUNTRIES.map((c) => (
                        <SelectItem key={c.name} value={c.name}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.birthCountry && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.birthCountry}
                    </p>
                  )}
                </div>
              </div>

              {nationality === INDONESIA && (
                <div>
                  <Label className="text-sm font-semibold text-gray-700 mb-2 block">
                    Are you a Batam Local with KTP? *
                  </Label>
                  <div className="flex items-center gap-6">
                    {["Yes", "No"].map((opt) => (
                      <label
                        key={opt}
                        className="flex items-center gap-2 cursor-pointer text-sm text-gray-700"
                      >
                        <input
                          type="radio"
                          name="isBatamLocal"
                          value={opt}
                          checked={isBatamLocal === opt}
                          onChange={() => setIsBatamLocal(opt)}
                          className="h-4 w-4 accent-[#0B2421] cursor-pointer"
                        />
                        {opt}
                      </label>
                    ))}
                  </div>
                  {errors.isBatamLocal && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.isBatamLocal}
                    </p>
                  )}
                </div>
              )}

              {nationality === INDONESIA && isBatamLocal === "Yes" && (
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-2">
                    Please upload your KTP for verification
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <KtpUploadBox
                      label="KTP Front View *"
                      value={ktpFrontUrl}
                      isUploading={isUploadingKtpFront}
                      onSelect={(file) => handleKtpUpload("front", file)}
                      error={errors.ktpFrontUrl}
                    />
                    <KtpUploadBox
                      label="KTP Back View *"
                      value={ktpBackUrl}
                      isUploading={isUploadingKtpBack}
                      onSelect={(file) => handleKtpUpload("back", file)}
                      error={errors.ktpBackUrl}
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-1.5">
                    JPEG, PNG, WEBP or PDF. 5MB total across both files.
                  </p>
                </div>
              )}

              <div>
                <Label className="text-sm font-semibold text-gray-700 mb-2 block">
                  Gender *
                </Label>
                <Select value={gender} onValueChange={setGender}>
                  <SelectTrigger className="h-12 text-gray-700 sm:w-1/2">
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent className="bg-white text-black">
                    {GENDER_OPTIONS.map((g) => (
                      <SelectItem key={g} value={g}>
                        {g}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.gender && (
                  <p className="text-xs text-red-500 mt-1">{errors.gender}</p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-semibold text-gray-700 mb-2 block">
                    Date of Birth *
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        className="w-full justify-start h-12 bg-transparent text-gray-500 border border-gray-200"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dateOfBirth
                          ? format(dateOfBirth, "dd MMM yyyy")
                          : "Select date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        className={""}
                        classNames={{}}
                        mode="single"
                        selected={dateOfBirth}
                        onSelect={setDateOfBirth}
                      />
                    </PopoverContent>
                  </Popover>
                  {errors.dateOfBirth && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.dateOfBirth}
                    </p>
                  )}
                </div>

                <div>
                  <Label className="text-sm font-semibold text-gray-700 mb-2 block">
                    Passport Date of Issue *
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        className={`w-full justify-start h-12 bg-transparent text-gray-500 border ${
                          errors.dateOfIssue
                            ? "border-red-400 ring-2 ring-red-100"
                            : "border-gray-200"
                        }`}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dateOfIssue
                          ? format(dateOfIssue, "dd MMM yyyy")
                          : "Select date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        className={""}
                        classNames={{}}
                        mode="single"
                        selected={dateOfIssue}
                        onSelect={setDateOfIssue}
                      />
                    </PopoverContent>
                  </Popover>
                  {errors.dateOfIssue && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.dateOfIssue}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-semibold text-gray-700 mb-2 block">
                    Passport Expiry Date *
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        className="w-full justify-start h-12 bg-transparent text-gray-500 border border-gray-200"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {expiryDate
                          ? format(expiryDate, "dd MMM yyyy")
                          : "Select date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        className={""}
                        classNames={{}}
                        mode="single"
                        selected={expiryDate}
                        onSelect={setExpiryDate}
                      />
                    </PopoverContent>
                  </Popover>
                  <p className="text-xs text-gray-500 mt-1">
                    Must not be more than 10 years from date of issue
                  </p>
                  {errors.expiryDate && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.expiryDate}
                    </p>
                  )}
                </div>
              </div>

              <div className="pt-4 flex gap-3">
                <Button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="w-full sm:w-auto hover:bg-[#0B2421]/85 h-12 text-white bg-[#0B2421]"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    "Save Passport Details"
                  )}
                </Button>
                {hasPassport && (
                  <Button
                    variant="outline"
                    onClick={handleCancel}
                    disabled={isSaving}
                    className="h-12"
                  >
                    Cancel
                  </Button>
                )}
              </div>
            </div>
          )}
        </Card>
      </motion.div>
      <StatusDialog
        open={dialog.open}
        onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
        variant={dialog.variant}
        title={dialog.title}
        description={dialog.description}
      />
    </>
  );
}

/**
 * Bordered upload box for a single KTP card image (front or back), with a
 * thumbnail preview + "Replace" once an image has been uploaded.
 * @param {{ label: string; value: string; isUploading: boolean; onSelect: (file: File) => void; error?: string }} props
 */
function KtpUploadBox({ label, value, isUploading, onSelect, error }) {
  const inputRef = /** @type {React.RefObject<HTMLInputElement>} */ (
    useRef(null)
  );
  const LabelAny = /** @type {React.ComponentType<any>} */ (RawLable);

  return (
    <div>
      <LabelAny className="text-sm font-semibold text-gray-700 mb-2 block">
        {label}
      </LabelAny>
      {value && !isUploading ? (
        <div className="relative group rounded-xl overflow-hidden border border-gray-200 h-40">
          {value.toLowerCase().includes(".pdf") ? (
            <div className="w-full h-full flex flex-col items-center justify-center gap-1.5 bg-gray-50">
              <FileText className="h-8 w-8 text-gray-400" />
              <span className="text-xs text-gray-500">PDF uploaded</span>
            </div>
          ) : (
            <img
              src={value}
              alt={label}
              className="w-full h-full object-cover"
            />
          )}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="px-4 py-2 rounded-lg bg-white text-sm font-semibold"
            >
              Replace
            </button>
          </div>
          <input
            ref={inputRef}
            type="file"
            accept="image/*,.avif,application/pdf"
            className="hidden"
            onChange={(e) => onSelect(e.target.files?.[0])}
          />
        </div>
      ) : (
        <label className="flex flex-col items-center justify-center h-40 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors">
          {isUploading ? (
            <>
              <Loader2 className="h-6 w-6 animate-spin text-gray-400 mb-2" />
              <span className="text-sm text-gray-500">Uploading...</span>
            </>
          ) : (
            <>
              <UploadCloud className="h-6 w-6 text-gray-400 mb-2" />
              <span className="text-sm text-gray-500">Click to upload</span>
            </>
          )}
          <input
            type="file"
            accept="image/*,.avif,application/pdf"
            className="hidden"
            onChange={(e) => onSelect(e.target.files?.[0])}
          />
        </label>
      )}
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}
